/**
 * PREVIEW & DOWNLOAD STEP - PROFESSIONAL VERSION
 * 
 * Features:
 * - Professional templates matching uploaded examples
 * - Color selection: DARK GREY or BLACK
 * - Two-column layout (PAY on left, TAXES on right)
 * - SUMMARY section with Current and YTD
 * - Overtime and Bonus display
 * - NON-NEGOTIABLE stamp
 * - CHANGE TEMPLATE option before download
 * - Email to Client feature
 */

import React, { useState, useRef } from 'react';
import LivePaystubPNGPreview from '../LivePaystubPNGPreview';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Edit3, Pencil } from 'lucide-react';
import {
  Download,
  FileText,
  Loader2,
  Grid,
  ChevronLeft,
  ChevronRight,
  Palette,
  Check,
  RefreshCw,
  X,
  Eye,
  FileDown,
  Mail,
  Send
} from 'lucide-react';
import axios from 'axios';
import { STATE_TAX_DATA } from '../../utils/taxCalculations';
import { PAYSTUB_TEMPLATES } from '../TemplateGallery';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate professional file name: {FirstInitial}{LastInitial}{MMDDYY}.pdf
const generateFileName = (employeeName, payDate) => {
  const nameParts = (employeeName || 'Unknown User').trim().split(/\s+/);
  const firstInitial = (nameParts[0]?.[0] || 'X').toUpperCase();
  const lastInitial = (nameParts[nameParts.length - 1]?.[0] || 'X').toUpperCase();

  let dateStr = '';
  if (payDate) {
    const [year, month, day] = payDate.split('-').map(Number);
    const yy = String(year).slice(-2);
    dateStr = `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${yy}`;
  } else {
    const now = new Date();
    dateStr = `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${String(now.getFullYear()).slice(-2)}`;
  }

  return `${firstInitial}${lastInitial}-${dateStr}.pdf`;
};

// Template Change Modal
const ChangeTemplateModal = ({ isOpen, onClose, templates, selectedTemplate, onSelectTemplate, colorScheme, onColorChange }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-900 rounded-xl border border-zinc-700 w-full max-w-4xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-zinc-800 p-4 border-b border-zinc-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Change Template</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Color Selector */}
        <div className="p-4 border-b border-zinc-700 flex items-center gap-4">
          <span className="text-zinc-400">Color:</span>
          <div className="flex gap-2">
            <button
              onClick={() => onColorChange('darkgrey')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                colorScheme === 'darkgrey'
                  ? 'bg-gray-600 text-white ring-2 ring-gray-400'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
            >
              DARK GREY {colorScheme === 'darkgrey' && <Check className="w-4 h-4" />}
            </button>
            <button
              onClick={() => onColorChange('black')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                colorScheme === 'black'
                  ? 'bg-zinc-900 text-white ring-2 ring-zinc-500'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
            >
              BLACK {colorScheme === 'black' && <Check className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Template Grid */}
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div
              key={t.id}
              onClick={() => {
                onSelectTemplate(t.id);
                toast.success(`Template changed to ${t.name}`);
                onClose();
              }}
              className={`cursor-pointer rounded-lg border-2 transition-all overflow-hidden ${
                selectedTemplate === t.id
                  ? 'border-amber-500 bg-amber-500/10 ring-2 ring-amber-500/50'
                  : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-500'
              }`}
            >
              <div className="h-32 bg-gray-100 p-2 relative">
                <div className="text-xs text-gray-800 h-full flex flex-col justify-between">
                  <div className="bg-gray-700 h-2 w-full mb-1"></div>
                  <div className="text-center font-bold text-gray-700">PREVIEW</div>
                  <div className="bg-gray-300 h-16 rounded"></div>
                </div>
              </div>
              <div className="p-3 bg-zinc-900">
                <p className="text-sm font-medium text-white">{t.name}</p>
                {selectedTemplate === t.id && (
                  <Badge className="bg-amber-500 text-xs mt-1">Selected</Badge>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-zinc-800 p-4 border-t border-zinc-700 flex justify-end">
          <Button onClick={onClose} className="bg-amber-600 hover:bg-amber-700">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function PreviewStep({ formData, updateFormData, onDownloadSuccess, onEditValues }) {
  const [selectedTemplate, setSelectedTemplate] = useState(formData.selectedTemplate || 1);
  const [colorScheme, setColorScheme] = useState(formData.colorScheme || 'darkgrey');
  // 2026-06-23 — Per-template color override applied to the generated PDF.
  // 'original' keeps the template's native palette.
  // 'bw' converts every drawn color to grayscale.
  // 'custom' dyes every non-grayscale element with `customColor`.
  const [colorMode, setColorMode] = useState(formData.colorMode || 'original');
  const [customColor, setCustomColor] = useState(formData.customColor || '#1d4ed8');
  // 2026-02 — Preview-time layout tuning without redeploy
  const [fontScale, setFontScale] = useState(formData.fontScale || 1.0);
  const [autoFit, setAutoFit] = useState(formData.autoFit !== false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewStub, setPreviewStub] = useState(0);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const previewRef = useRef(null);

  const selectTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    updateFormData({ selectedTemplate: templateId });
  };

  const handleColorChange = (color) => {
    setColorScheme(color);
    updateFormData({ colorScheme: color });
  };

  const getColor = () => {
    return colorScheme === 'darkgrey' ? '#4b5563' : '#18181b';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
  };

  const formatCurrency = (amount) => {
    return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Validate formData before generation - returns warnings and critical errors
  const validateFormData = () => {
    const warnings = [];
    const critical = [];
    
    // Critical: Must have paystub data - this is the ONLY thing that blocks generation
    if (!formData.paystubs || formData.paystubs.length === 0) {
      critical.push('No paystub data - go back to Paystub Details step');
    } else {
      const firstStub = formData.paystubs[0];
      if (!firstStub.current && !firstStub.grossPay) {
        critical.push('Paystub missing pay data');
      }
    }
    
    // Warnings are informational only - NOT checking payType, payFrequency etc
    // since these don't appear directly on the paystub
    if (!formData.companyName?.trim()) warnings.push('Company Name');
    if (!formData.employeeName?.trim()) warnings.push('Employee Name');
    
    return { warnings, critical };
  };

  // Download ALL stubs as one PDF - NEVER BLOCK, always generate
  const handleDownloadAll = async (forceGenerate = false) => {
    const { critical } = validateFormData();
    
    // Only critical errors (no paystub data) can block
    if (critical.length > 0) {
      toast.error(`Cannot generate: ${critical.join(', ')}`);
      return;
    }
    
    // Always proceed with generation
    setIsGenerating(true);

    try {
      // Generate PDF with retry and longer timeout
      let response;
      let retries = 2;
      
      while (retries > 0) {
        try {
          response = await axios.post(
            `${API}/api/paystub/generate-pdf`,
            {
              formData: { ...formData, colorScheme },
              templateId: selectedTemplate,
              colorMode,
              accentHex: colorMode === 'custom' ? customColor : null,
              fontScale,
              autoFit,
            },
            { 
              responseType: 'blob',
              timeout: 60000 // 60 second timeout for PDF generation
            }
          );
          break; // Success, exit loop
        } catch (retryError) {
          retries--;
          if (retries === 0) throw retryError;
          console.log('PDF generation retry...');
          toast.info('Retrying PDF generation...');
          await new Promise(r => setTimeout(r, 2000)); // Wait 2 seconds before retry
        }
      }

      // Check if response is actually a PDF (not an error)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Server returned an error');
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const firstStub = formData.paystubs?.[0];
      const fileName = generateFileName(formData.employeeName || 'Paystub', firstStub?.payDate);
      const allFileName = formData.paystubs?.length > 1
        ? fileName.replace('.pdf', '_all.pdf')
        : fileName;

      link.setAttribute('download', allFileName);
      document.body.appendChild(link);

      // 2026-02-08 iOS fix — commit the W-2 prompt state BEFORE the browser
      // hands control to the PDF viewer. On iPhone Safari, `link.click()`
      // navigates the tab to display the blob PDF, freezing/discarding any
      // React state changes queued after it. Calling onDownloadSuccess()
      // first guarantees the prompt is in the DOM before that hop.
      if (onDownloadSuccess) {
        onDownloadSuccess();
      }

      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded ${formData.numberOfStubs || formData.paystubs?.length || 1} paystub(s) as ${allFileName}`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate PDF';
      toast.error(errorMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  // Download SINGLE stub as individual PDF - NEVER BLOCK
  const handleDownloadSingle = async (stubIndex) => {
    const { critical } = validateFormData();
    
    if (critical.length > 0) {
      toast.error(`Cannot generate: ${critical.join(', ')}`);
      return;
    }
    
    setIsGenerating(true);

    try {
      const stub = formData.paystubs[stubIndex];

      const singleStubFormData = {
        ...formData,
        colorScheme,
        paystubs: [stub],
        numberOfStubs: 1
      };

      const response = await axios.post(
        `${API}/api/paystub/generate-pdf`,
        {
          formData: singleStubFormData,
          templateId: selectedTemplate,
          colorMode,
          accentHex: colorMode === 'custom' ? customColor : null,
          fontScale,
          autoFit,
        },
        { responseType: 'blob' }
      );

      // Check if response is actually a PDF (not an error)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Server returned an error');
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const fileName = generateFileName(formData.employeeName || 'Paystub', stub?.payDate);

      link.setAttribute('download', fileName);
      document.body.appendChild(link);

      // 2026-02-08 iOS fix — see handleDownloadAll for full explanation.
      if (onDownloadSuccess) {
        onDownloadSuccess();
      }

      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded stub #${stubIndex + 1} as ${fileName}`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate PDF';
      toast.error(errorMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  // Email paystubs to client
  const handleEmailToClient = async () => {
    if (!clientEmail || !clientEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSendingEmail(true);
    
    try {
      const response = await axios.post(`${API}/api/paystub/email`, {
        formData: { ...formData, colorScheme },
        templateId: selectedTemplate,
        clientEmail: clientEmail,
        clientName: formData.employeeName,
        stubIndices: null // Send all stubs
      });
      
      if (response.data.success) {
        toast.success(`Paystub${response.data.stubs_sent > 1 ? 's' : ''} sent to ${clientEmail}`);
        setShowEmailModal(false);
        setClientEmail('');
        
        if (onDownloadSuccess) {
          onDownloadSuccess();
        }
      }
    } catch (error) {
      console.error('Error emailing paystub:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Convert number to words for check
  const numberToWords = (amount) => {
    const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
      'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN',
      'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
    const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

    const dollars = Math.floor(amount || 0);
    const cents = Math.round(((amount || 0) - dollars) * 100);

    let dollarWords = '';
    if (dollars === 0) {
      dollarWords = 'ZERO';
    } else if (dollars < 20) {
      dollarWords = ones[dollars];
    } else if (dollars < 100) {
      dollarWords = tens[Math.floor(dollars / 10)] + (dollars % 10 ? ' ' + ones[dollars % 10] : '');
    } else if (dollars < 1000) {
      dollarWords = ones[Math.floor(dollars / 100)] + ' HUNDRED';
      const remainder = dollars % 100;
      if (remainder) {
        if (remainder < 20) {
          dollarWords += ' ' + ones[remainder];
        } else {
          dollarWords += ' ' + tens[Math.floor(remainder / 10)] + (remainder % 10 ? ' ' + ones[remainder % 10] : '');
        }
      }
    } else {
      const thousands = Math.floor(dollars / 1000);
      const remainder = dollars % 1000;
      if (thousands < 20) {
        dollarWords = ones[thousands] + ' THOUSAND';
      } else {
        dollarWords = tens[Math.floor(thousands / 10)] + (thousands % 10 ? ' ' + ones[thousands % 10] : '') + ' THOUSAND';
      }
      if (remainder) {
        if (remainder < 100) {
          if (remainder < 20) {
            dollarWords += ' ' + ones[remainder];
          } else {
            dollarWords += ' ' + tens[Math.floor(remainder / 10)] + (remainder % 10 ? ' ' + ones[remainder % 10] : '');
          }
        } else {
          dollarWords += ' ' + ones[Math.floor(remainder / 100)] + ' HUNDRED';
          const subRemainder = remainder % 100;
          if (subRemainder) {
            if (subRemainder < 20) {
              dollarWords += ' ' + ones[subRemainder];
            } else {
              dollarWords += ' ' + tens[Math.floor(subRemainder / 10)] + (subRemainder % 10 ? ' ' + ones[subRemainder % 10] : '');
            }
          }
        }
      }
    }

    return `${dollarWords} AND ${cents}/100 DOLLARS`;
  };

  const goToPrevStub = () => setPreviewStub(Math.max(0, previewStub - 1));
  const goToNextStub = () => setPreviewStub(Math.min(formData.paystubs.length - 1, previewStub + 1));

  // Generate PDF Preview (show exact PDF before download)
  const handlePreviewPdf = async () => {
    setIsLoadingPreview(true);
    
    try {
      const response = await axios.post(
        `${API}/api/paystub/generate-pdf`,
        {
          formData: { ...formData, colorScheme },
          templateId: selectedTemplate,
          colorMode,
          accentHex: colorMode === 'custom' ? customColor : null,
          fontScale,
          autoFit,
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview. Please try again.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Close PDF Preview
  const closePdfPreview = () => {
    setShowPdfPreview(false);
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  const template = PAYSTUB_TEMPLATES.find(t => t.id === selectedTemplate);
  const currentStub = formData.paystubs?.[previewStub];
  const stateName = STATE_TAX_DATA[formData.taxState]?.name || formData.taxState;
  const primaryColor = getColor();

  return (
    <div className="space-y-6">
      {/* 2026-02-XX (user request "streamlined") — FINAL NUMBERS REVIEW.
          Shows the exact numbers that will print on every PDF so the
          user can confirm everything is right BEFORE downloading.  A
          single "Edit values" button jumps back to Paystub Details
          (step 4) if any adjustment is needed. */}
      {formData.paystubs?.length > 0 && (
        <Card className="bg-gradient-to-br from-emerald-950/40 to-zinc-900 border-emerald-500/40" data-testid="final-numbers-review">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-emerald-300" />
                </div>
                <div className="flex flex-col">
                  <span className="text-base">Final Numbers Review</span>
                  <span className="text-xs text-zinc-400 font-normal mt-0.5">These are the exact values the PDF will print</span>
                </div>
              </div>
              {onEditValues && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onEditValues}
                  className="border-amber-500/50 text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 h-8"
                  data-testid="jump-to-edit-values-btn"
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit Values
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-zinc-500 border-b border-zinc-800">
                    <th className="text-left py-1.5 pr-2 font-medium">Stub</th>
                    <th className="text-left py-1.5 px-2 font-medium">Pay Date</th>
                    <th className="text-right py-1.5 px-2 font-medium">Current Gross</th>
                    <th className="text-right py-1.5 px-2 font-medium">Current Net</th>
                    <th className="text-right py-1.5 px-2 font-medium text-emerald-300">YTD Gross</th>
                    <th className="text-right py-1.5 px-2 font-medium text-emerald-300">YTD Net</th>
                    <th className="text-right py-1.5 pl-2 font-medium text-amber-300">Edits</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.paystubs.map((s, idx) => {
                    const stubNum = (parseInt(formData.stubNumberInYear) || 1) - idx;
                    const edits = (s.ytdOverrides ? Object.keys(s.ytdOverrides).length : 0) + (s.ytdOverride ? 1 : 0);
                    const fmt = (n) => (Number(n || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    return (
                      <tr key={idx} className="border-b border-zinc-800/60 last:border-0" data-testid={`final-review-row-${idx}`}>
                        <td className="py-1.5 pr-2 text-zinc-300">#{stubNum}</td>
                        <td className="py-1.5 px-2 text-zinc-400">{s.payDate || '—'}</td>
                        <td className="py-1.5 px-2 text-right text-white">${fmt(s.current?.grossPay)}</td>
                        <td className="py-1.5 px-2 text-right text-white">${fmt(s.current?.netPay)}</td>
                        <td className="py-1.5 px-2 text-right text-emerald-300 font-semibold">${fmt(s.ytd?.grossPay)}</td>
                        <td className="py-1.5 px-2 text-right text-emerald-300 font-semibold">${fmt(s.ytd?.netPay)}</td>
                        <td className="py-1.5 pl-2 text-right">
                          {edits > 0
                            ? <span className="text-amber-300 text-[10px] bg-amber-500/15 border border-amber-500/40 rounded px-1.5 py-0.5">{edits} edit{edits > 1 ? 's' : ''}</span>
                            : <span className="text-zinc-600 text-[10px]">auto</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">
              Something off?  Tap <span className="text-amber-300 font-semibold">Edit Values</span> to adjust any amount — then come back here.
            </p>
          </CardContent>
        </Card>
      )}

      {/* DATA VALIDATION CHECK - Simple: either ready or no data */}
      {(() => {
        const { critical } = validateFormData();
        
        // Only show error if there's NO paystub data at all
        if (critical.length > 0) {
          return (
            <Card className="bg-red-900/30 border-red-600/50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <X className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-red-400">Cannot Generate</h3>
                    <p className="text-red-200 text-sm mt-1">{critical[0]}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        }
        
        // Ready to generate!
        return (
          <Card className="bg-green-900/30 border-green-600/50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-400">Ready to Generate!</h3>
                  <p className="text-sm text-green-300">
                    Click Download to generate your paystub(s).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* CHANGE TEMPLATE BANNER */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white">Preview Your Paystub</h3>
              <p className="text-sm text-zinc-400">
                Template: <span className="text-amber-400">{template?.name}</span>
                {' • '}
                Color: <span className="text-amber-400">{colorScheme.toUpperCase()}</span>
              </p>
            </div>
            <Button
              onClick={() => setShowTemplateModal(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="change-template-btn"
            >
              <Palette className="w-4 h-4 mr-2" />
              Change Template
            </Button>
          </div>
          
          {/* Template Style Indicator */}
          <div className="mt-4 p-3 bg-zinc-800 rounded-lg border border-zinc-700">
            <div className="text-xs text-zinc-400 mb-2">Template Style Preview:</div>
            <div className="flex items-center gap-4">
              {/* Mini Template Preview based on style */}
              <div className="w-24 h-32 bg-white rounded overflow-hidden shadow-md flex-shrink-0" style={{ fontSize: '4px' }}>
                {template?.style === 'classic_check' && (
                  <div className="h-full flex flex-col">
                    <div className="bg-blue-600 text-white p-1 text-center font-bold">CHECK</div>
                    <div className="p-1 flex-1 border-b">
                      <div className="text-gray-600">Pay to: Employee</div>
                      <div className="mt-1 border p-0.5 text-center font-bold">$$$</div>
                    </div>
                    <div className="bg-blue-600 text-white p-0.5 text-center">EARNINGS</div>
                    <div className="p-1 text-gray-500 flex-1">Details...</div>
                  </div>
                )}
                {template?.style === 'corporate_modern' && (
                  <div className="h-full flex flex-col">
                    <div className="bg-gray-800 text-white p-1">
                      <div className="font-bold">COMPANY</div>
                      <div className="text-gray-400">Address</div>
                    </div>
                    <div className="p-1 flex-1">
                      <div className="grid grid-cols-2 gap-0.5">
                        <div className="bg-gray-100 p-0.5">Employee</div>
                        <div className="bg-gray-100 p-0.5">Period</div>
                      </div>
                      <div className="mt-1 text-gray-500">Earnings table</div>
                    </div>
                    <div className="bg-green-600 text-white p-1 text-center font-bold">NET: $$$</div>
                  </div>
                )}
                {template?.style === 'clean_prominent' && (
                  <div className="h-full flex flex-col">
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1 text-center">
                      <div className="font-bold">NET PAY</div>
                      <div className="text-lg">$$$</div>
                    </div>
                    <div className="grid grid-cols-2 flex-1">
                      <div className="p-1 border-r">
                        <div className="font-bold text-green-600">Earnings</div>
                        <div className="text-gray-400">items...</div>
                      </div>
                      <div className="p-1">
                        <div className="font-bold text-red-600">Deductions</div>
                        <div className="text-gray-400">items...</div>
                      </div>
                    </div>
                  </div>
                )}
                {template?.style === 'official_govt' && (
                  <div className="h-full flex flex-col border-2 border-double border-gray-600">
                    <div className="bg-gray-100 p-1 text-center border-b">
                      <div className="flex justify-center gap-1">
                        <span className="w-2 h-2 rounded-full border border-gray-600"></span>
                        <span className="font-bold">OFFICIAL</span>
                        <span className="w-2 h-2 rounded-full border border-gray-600"></span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 border-b p-0.5">
                      <div className="border-r pr-0.5">Employer</div>
                      <div className="pl-0.5">Employee</div>
                    </div>
                    <div className="flex-1 p-1">
                      <div className="border border-gray-300 p-0.5 mb-1">Gross</div>
                      <div className="border border-gray-300 p-0.5">Deductions</div>
                    </div>
                    <div className="bg-gray-800 text-white p-1 text-center">NET PAY</div>
                  </div>
                )}
                {template?.style === 'modern_minimal' && (
                  <div className="h-full flex flex-col">
                    <div className="p-1">
                      <div className="font-bold">Company Name</div>
                      <div className="text-gray-400">Clean & Simple</div>
                    </div>
                    <div className="flex-1 p-1">
                      <div className="bg-gray-100 p-1 rounded mb-1">
                        <div className="text-center text-2xl font-bold">$$$</div>
                        <div className="text-center text-gray-500">NET PAY</div>
                      </div>
                      <div className="grid grid-cols-3 text-center text-gray-500">
                        <div>Desc</div><div>Curr</div><div>YTD</div>
                      </div>
                    </div>
                  </div>
                )}
                {template?.style === 'traditional_voucher' && (
                  <div className="h-full flex flex-col">
                    <div className="border-b-2 border-dashed border-gray-400 h-1"></div>
                    <div className="p-1 bg-gradient-to-r from-gray-100 to-white border-b">
                      <div className="flex justify-between">
                        <div className="font-bold">Company</div>
                        <div className="border border-gray-400 px-1">CHK#</div>
                      </div>
                    </div>
                    <div className="p-1 flex-1">
                      <div className="text-gray-600 mb-1">Pay To: Employee</div>
                      <div className="grid grid-cols-5 border text-center">
                        <div className="bg-gray-200 border-r">Hrs</div>
                        <div className="bg-gray-200 border-r">Rate</div>
                        <div className="bg-gray-200 border-r">Gross</div>
                        <div className="bg-gray-200 border-r">Ded</div>
                        <div className="bg-gray-200">Net</div>
                      </div>
                    </div>
                    <div className="border-t-2 border-dashed border-gray-400 h-1"></div>
                    <div className="bg-gray-100 text-center p-0.5 text-gray-500">DETACH</div>
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <div className="text-white font-medium">{template?.name}</div>
                <div className="text-xs text-zinc-400 mt-1">
                  {template?.style === 'classic_check' && 'Traditional check format with tear-off stub. Perfect for standard payroll processing.'}
                  {template?.style === 'corporate_modern' && 'Sleek corporate design with dark header. Professional look for modern businesses.'}
                  {template?.style === 'clean_prominent' && 'Large net pay display with side-by-side earnings/deductions. Clear and easy to read.'}
                  {template?.style === 'official_govt' && 'Government-style official document with formal borders and seal design.'}
                  {template?.style === 'modern_minimal' && 'Clean, minimalist design focusing on key information. Simple and elegant.'}
                  {template?.style === 'traditional_voucher' && 'Classic voucher style with perforated edges. Traditional payroll look.'}
                </div>
                {template?.hasCheckStub && (
                  <div className="text-xs text-green-400 mt-1">✓ Includes check stub</div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LIVE PDF PREVIEW — renders the user's ACTUAL form data through the
          backend so what they see here is byte-for-byte what they'll download.
          Refreshes whenever selectedTemplate, colorScheme, or the active
          paystub index changes. */}
      <LivePaystubPNGPreview
        formData={formData}
        templateId={selectedTemplate}
        previewStub={previewStub}
        onChangeTemplate={() => setShowTemplateModal(true)}
        colorMode={colorMode}
        accentHex={colorMode === 'custom' ? customColor : null}
      />

      {/* Stub Navigation */}
      {formData.paystubs?.length > 1 && (
        <div className="flex gap-2 flex-wrap justify-center">
          {formData.paystubs.map((_, index) => (
            <Button
              key={index}
              variant={previewStub === index ? 'default' : 'outline'}
              onClick={() => setPreviewStub(index)}
              className={previewStub === index ? 'bg-amber-600' : 'border-zinc-600'}
            >
              Stub #{index + 1}
            </Button>
          ))}
        </div>
      )}

      {/* Legacy HTML mockup removed 2026-02 — LivePaystubPNGPreview above
          is now the single source of truth for what the user will download. */}

      {/* Action Buttons */}
      <div className="space-y-4">
        {/* Individual Stub Downloads */}
        {formData.paystubs?.length > 1 && (
          <Card className="bg-zinc-900 border-zinc-700">
            <CardContent className="pt-4">
              <h4 className="text-white font-medium mb-3">Download Individual Stubs</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {formData.paystubs.map((stub, index) => {
                  const fileName = generateFileName(formData.employeeName, stub?.payDate);
                  return (
                    <Button
                      key={index}
                      onClick={() => handleDownloadSingle(index)}
                      disabled={isGenerating}
                      variant="outline"
                      className="border-blue-500 text-blue-400 hover:bg-blue-500/10 flex flex-col items-center py-4 h-auto"
                      data-testid={`download-stub-${index}`}
                    >
                      <FileDown className="w-4 h-4 mb-1" />
                      <span className="text-xs">Stub #{index + 1}</span>
                      <span className="text-[10px] text-zinc-500">{formatDate(stub?.payDate)}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 2026-06-23 — Color theme picker.  Sits right next to "Change Template"
            so the user can flip between Original / Black & White / a custom
            color WITHOUT having to leave the Preview step. */}
        <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-4" data-testid="color-mode-panel">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-4 h-4 text-amber-400" />
            <h4 className="text-sm font-bold text-white">Color theme</h4>
            <span className="text-[10px] uppercase tracking-wider text-zinc-400 bg-zinc-800 border border-zinc-700 rounded-full px-2 py-0.5">applies to all stubs</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => { setColorMode('original'); updateFormData({ colorMode: 'original' }); }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${colorMode === 'original' ? 'border-amber-500 bg-amber-500/15 text-amber-200' : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-600'}`}
              data-testid="color-mode-original"
            >
              🎨 Original
            </button>
            <button
              type="button"
              onClick={() => { setColorMode('bw'); updateFormData({ colorMode: 'bw' }); }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${colorMode === 'bw' ? 'border-zinc-300 bg-zinc-300/15 text-zinc-100' : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-600'}`}
              data-testid="color-mode-bw"
            >
              ⬛ Black &amp; White
            </button>
            <button
              type="button"
              onClick={() => { setColorMode('custom'); updateFormData({ colorMode: 'custom', customColor }); }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${colorMode === 'custom' ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200' : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-600'}`}
              data-testid="color-mode-custom"
            >
              🌈 Custom
            </button>
            {colorMode === 'custom' && (
              <div className="flex items-center gap-2 ml-2 px-3 py-1.5 rounded-lg bg-zinc-900 border border-emerald-500/40">
                <span className="text-xs text-zinc-400">Pick color</span>
                <input
                  type="color"
                  value={customColor}
                  onChange={(e) => { setCustomColor(e.target.value); updateFormData({ customColor: e.target.value }); }}
                  className="w-9 h-7 rounded border border-zinc-700 cursor-pointer bg-transparent"
                  data-testid="color-mode-custom-picker"
                  title="Choose any color"
                />
                <span className="text-xs font-mono text-emerald-300">{customColor.toUpperCase()}</span>
              </div>
            )}
            <span className="text-[11px] text-zinc-500 ml-auto">
              {colorMode === 'original' && "✓ Keeps each template's built-in colors."}
              {colorMode === 'bw' && '✓ Every color becomes grayscale — great for printing.'}
              {colorMode === 'custom' && '✓ All colored parts will use the color above.'}
            </span>
          </div>
        </div>

        {/* 2026-02 — Layout tuning panel (post-preview edit) */}
        <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-4" data-testid="layout-tune-panel">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="w-4 h-4 rounded-full bg-gradient-to-br from-fuchsia-500 to-cyan-500 inline-block" />
            <h4 className="text-sm font-bold text-white">Fine-Tune Layout</h4>
            <span className="text-[10px] uppercase tracking-wider text-zinc-400 bg-zinc-800 border border-zinc-700 rounded-full px-2 py-0.5">only if something looks off</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Font scale presets */}
            <div className="flex items-center gap-1.5 bg-zinc-950 rounded-lg p-1 border border-zinc-800">
              <span className="text-[11px] text-zinc-500 px-1.5">Text Size</span>
              {[
                { v: 0.85, label: 'XS' },
                { v: 0.92, label: 'S' },
                { v: 1.0, label: 'M' },
                { v: 1.08, label: 'L' },
                { v: 1.15, label: 'XL' },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => { setFontScale(opt.v); updateFormData({ fontScale: opt.v }); }}
                  className={`px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${
                    Math.abs(fontScale - opt.v) < 0.02
                      ? 'bg-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/30'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                  data-testid={`layout-scale-${opt.label.toLowerCase()}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {/* Auto-fit toggle */}
            <button
              type="button"
              onClick={() => { setAutoFit(!autoFit); updateFormData({ autoFit: !autoFit }); }}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-colors ${
                autoFit
                  ? 'border-emerald-500 bg-emerald-500/15 text-emerald-200'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600'
              }`}
              data-testid="layout-autofit-toggle"
            >
              <span className={`w-2 h-2 rounded-full ${autoFit ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
              Auto-fit long names {autoFit ? 'ON' : 'OFF'}
            </button>
            <span className="text-[11px] text-zinc-500 ml-auto">
              Tap <em>Preview</em> below to see changes.
            </span>
          </div>
        </div>

        {/* Main Action Buttons */}
        <div className="flex flex-wrap gap-3 justify-center">
          <Button
            onClick={() => setShowTemplateModal(true)}
            variant="outline"
            className="border-amber-500 text-amber-400 hover:bg-amber-500/10 px-6 py-6"
            data-testid="change-template-btn-bottom"
          >
            <Palette className="w-5 h-5 mr-2" />
            Change Template
          </Button>
          
          {/* PREVIEW PDF BUTTON - Shows exact PDF before download */}
          <Button
            onClick={handlePreviewPdf}
            disabled={isLoadingPreview || isGenerating}
            variant="outline"
            className="border-purple-500 text-purple-400 hover:bg-purple-500/10 px-6 py-6"
            data-testid="preview-pdf-btn"
          >
            {isLoadingPreview ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Loading Preview...
              </>
            ) : (
              <>
                <Eye className="w-5 h-5 mr-2" />
                Preview PDF
              </>
            )}
          </Button>
          
          <Button
            onClick={() => setShowEmailModal(true)}
            disabled={isGenerating}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-6"
            data-testid="email-paystubs-btn"
          >
            <Mail className="w-5 h-5 mr-2" />
            Email to Client
          </Button>

          <Button
            onClick={handleDownloadAll}
            disabled={isGenerating}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
            data-testid="download-paystubs-btn"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                {formData.paystubs?.length > 1
                  ? `Download All ${formData.paystubs?.length} Stubs (PDF)`
                  : 'Download Paystub (PDF)'
                }
              </>
            )}
          </Button>
        </div>

        {/* File Naming Info */}
        <p className="text-center text-xs text-zinc-500">
          Files named as: {generateFileName(formData.employeeName || 'Your Name', formData.paystubs?.[0]?.payDate)}
          (initials + date)
        </p>
      </div>

      {/* Change Template Modal */}
      <ChangeTemplateModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        templates={PAYSTUB_TEMPLATES}
        selectedTemplate={selectedTemplate}
        onSelectTemplate={selectTemplate}
        colorScheme={colorScheme}
        onColorChange={handleColorChange}
      />

      {/* Email to Client Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl border border-zinc-700 w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-6 h-6 text-white" />
                  <h3 className="text-lg font-semibold text-white">Email Payday ST to Client</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowEmailModal(false)}
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Client Email Address</Label>
                <Input
                  type="email"
                  placeholder="client@email.com"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  className="bg-zinc-800 border-zinc-600"
                  data-testid="client-email-input"
                />
              </div>
              
              {/* Info Box */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-sm text-blue-300">
                  <strong>SNAP Verification Safe:</strong> The exact same PDF that would be downloaded 
                  is sent as an email attachment. No compression or modification occurs.
                </p>
              </div>
              
              <div className="text-sm text-zinc-400">
                <p><strong>Employee:</strong> {formData.employeeName}</p>
                <p><strong>Company:</strong> {formData.companyName}</p>
                <p><strong>Stubs:</strong> {formData.paystubs?.length || formData.numberOfStubs || 1}</p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="border-t border-zinc-700 p-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowEmailModal(false)}
                className="border-zinc-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEmailToClient}
                disabled={isSendingEmail || !clientEmail}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="send-email-btn"
              >
                {isSendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal - Shows EXACT PDF before download */}
      {showPdfPreview && pdfPreviewUrl && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl border border-zinc-700 w-full max-w-5xl h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Eye className="w-6 h-6 text-white" />
                <h3 className="text-lg font-semibold text-white">PDF Preview - Exactly What You&apos;ll Download</h3>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleDownloadAll}
                  disabled={isGenerating}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closePdfPreview}
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* PDF Viewer */}
            <div className="flex-1 bg-gray-800">
              <iframe
                src={pdfPreviewUrl}
                title="PDF Preview"
                className="w-full h-full"
                style={{ border: 'none' }}
              />
            </div>
            
            {/* Footer */}
            <div className="border-t border-zinc-700 p-3 bg-zinc-800 flex justify-between items-center">
              <p className="text-sm text-zinc-400">
                This is the exact PDF that will be downloaded or emailed.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={closePdfPreview}
                  className="border-zinc-600"
                >
                  Close Preview
                </Button>
                <Button
                  onClick={handleDownloadAll}
                  disabled={isGenerating}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
